import {PAYMENT_KEYS} from "@/api/paymentApi";
import Modal from "@/components/Common/Modal";
import Loader from "@/components/Common/kpi/Loader";
import {useFetchUser} from "@/hooks/useFetchUser";
import usePackageLimits from "@/hooks/usePackageLimits";
import {usePriceFormat} from "@/hooks/usePriceFormat";
import {useGenericQuery} from "@/hooks/useQueryHooks";
import {client} from "@/httpClient";
import {isMoreBuyoutsModalOpenAtom} from "@/store/jotaiAtoms";
import {handleErrorWithToast} from "@/utils/errorHandlers";
import {useMutation} from "@tanstack/react-query";
import type {AxiosError} from "axios";
import {useAtom} from "jotai";
import {RESET} from "jotai/utils";
import {type FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

type TContentBundle = {
  active: boolean;
  buyout: number;
  campaigns: number;
  created: string;
  id: number;
  is_most_popular: boolean;
  modified: string;
  name: string;
  prices: {
    currency: string;
    id: number;
    price: number;
    stripe_price_id: string;
  }[];
  seats: number;
  stripe_product_id: string;
  stripe_tax_id: string;
  vat?: number;
};

const AddMoreBuyoutsModal: FC = () => {
  const {t} = useTranslation(["payments", "common"]);
  const {currency} = usePackageLimits();
  const [modalAtom, setModalAtom] = useAtom(isMoreBuyoutsModalOpenAtom);
  const formatPrice = usePriceFormat({
    fractionDigits: 2,
  });
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const {refetch} = useFetchUser();

  const {data: bundles, isLoading} = useGenericQuery<TContentBundle[]>({
    queryKey: PAYMENT_KEYS.qBuyoutBundleList(`currency=${currency}&bundle_type=${modalAtom.type}`),
    enabled: !!currency,
  });

  const {mutateAsync} = useMutation({
    mutationKey: PAYMENT_KEYS.mBuyoutBundleOneTimePayment,
    mutationFn: ({price_id}: {price_id: number}) =>
      client.post(PAYMENT_KEYS.mBuyoutBundleOneTimePayment[0], {
        price_id,
        success_url: window.location.href,
        cancel_url: window.location.href,
      }),
    onSuccess: response => {
      toast.success(t("common:toastGlobalSuccess"));

      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      } else {
        setTimeout(() => refetch(), 2000);
      }
    },
    onError: (error: AxiosError<{detail: string}>) => handleErrorWithToast(error),
  });

  return (
    <Modal
      onClose={() => setModalAtom(RESET)}
      modalTitle={t(`${modalAtom.type}_add_more`)}
      onConfirm={async () =>
        await mutateAsync({
          price_id: bundles?.[selectedOption!].prices[0].id!,
        })
      }
      isConfirmDisabled={selectedOption === null}>
      <div className="flex w-full flex-col gap-4">
        <span className="text-neutral-grey-dark text-sm font-normal">{t(`${modalAtom.type}_choose`)}</span>
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {isLoading ? (
            <Loader
              styles={{
                loaderClassName: "h-12 w-12",
                containerClassName: "lg:col-span-2 items-center justify-center flex h-72 w-full",
              }}
            />
          ) : (
            (bundles ?? []).map((bundle, index) => {
              const price = +(bundle.prices[0].price * (1 + (bundle.vat ?? 0) / 100)).toFixed(2);
              const count =
                modalAtom.type === "EXTRA_BUYOUTS"
                  ? bundle.buyout
                  : modalAtom.type === "EXTRA_SEATS"
                    ? bundle.seats
                    : bundle.campaigns;

              return (
                <button
                  onClick={() => {
                    if (selectedOption === index) setSelectedOption(null);
                    else setSelectedOption(index);
                  }}
                  key={index}
                  className={
                    "group border-neutral-white-dark hover:border-primary-medium-solid relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-white p-5 transition-all duration-150 hover:shadow-md " +
                    (index === selectedOption ? "border-primary-dark-solid" : "")
                  }>
                  {bundle.is_most_popular && (
                    <div className="border-neutral-white-dark absolute -top-4 right-0 left-0 mx-auto w-min rounded-full border bg-white px-3 py-1 shadow-xs">
                      <span className="bg-linear-(--primary-dark-gradient) bg-clip-text text-sm whitespace-nowrap text-transparent">
                        {t(`${modalAtom.type}_most_popular`)}
                      </span>
                    </div>
                  )}
                  <div className="flex w-full flex-col items-start gap-1">
                    <span
                      className={
                        "group-hover:primary-medium-solid text-neutral-black group-hover:text-primary-medium-solid font-semibold transition-all duration-150 " +
                        (selectedOption === index ? "text-primary-dark-solid" : "")
                      }>
                      {t(`${modalAtom.type}_x`, {count})}
                    </span>
                    <span className="text-neutral-grey-dark text-left text-sm whitespace-nowrap">
                      {formatPrice(price)}{" "}
                      {t(`${modalAtom.type}_disclaimer`, {
                        count: +(price / count).toFixed(2),
                      })}
                    </span>
                  </div>
                  <div
                    className={
                      "border-neutral-grey-light group-hover:border-primary-medium-solid h-5 w-5 shrink-0 rounded-full border transition-all duration-150 " +
                      (selectedOption === index ? "border-primary-dark-solid drop-shadow-xs" : "")
                    }>
                    <div
                      className={
                        "h-full w-full rounded-full border border-white transition-all duration-150 " +
                        (selectedOption === index
                          ? "bg-primary-dark-solid group-hover:bg-primary-medium-solid"
                          : "bg-white")
                      }></div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddMoreBuyoutsModal;
